import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit, Download, Mail, MessageCircle, CheckCircle,
  XCircle, FileText, Clock, Package, Phone, Building,
  IndianRupee, Trash2, AlignLeft, Activity, Loader2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import { debitNotesAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';

const STATUS_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-700',
  ISSUED: 'bg-blue-100 text-blue-700',
  ADJUSTED: 'bg-emerald-100 text-emerald-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

const REASON_LABELS = {
  GOODS_RETURNED: 'Goods Returned to Supplier',
  PRICE_DIFFERENCE: 'Price Difference / Overcharge',
  QUALITY_ISSUE: 'Quality Issue / Defect',
  SHORTAGE: 'Shortage in Delivery',
  DAMAGE: 'Damaged Goods',
  OTHER: 'Other',
};

const DebitNoteDetailPage = () => {
  const { businessId, noteId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid && noteId) {
      fetchNote();
    }
  }, [bid, noteId]);

  const fetchNote = async () => {
    try {
      setLoading(true);
      const response = await debitNotesAPI.get(noteId);
      setNote(response.data.debit_note || response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load debit note'));
    } finally {
      setLoading(false);
    }
  };

  /* -------- Actions -------- */

  const handleIssue = () => {
    setConfirmModal({
      title: 'Issue Debit Note',
      description: `Issue ${note?.note_number || note?.debit_note_number}? This will send the note to the supplier.`,
      confirmLabel: 'Issue Note',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: CheckCircle,
      iconBgClass: 'bg-blue-100',
      iconClass: 'text-blue-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await debitNotesAPI.issue(noteId);
          toast.success('Debit note issued');
          fetchNote();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to issue debit note'));
        }
      },
    });
  };

  const handleCancel = () => {
    setCancelReason('');
    setConfirmModal({
      title: 'Cancel Debit Note',
      description: `Cancel ${note?.note_number || note?.debit_note_number}? This cannot be undone.`,
      confirmLabel: 'Cancel Note',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      isCancelFlow: true,
      onConfirm: async (reason) => {
        setConfirmModal(null);
        try {
          await debitNotesAPI.cancel(noteId, reason);
          toast.success('Debit note cancelled');
          setCancelReason('');
          fetchNote();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to cancel debit note'));
        }
      },
    });
  };

  const handleDelete = () => {
    setConfirmModal({
      title: 'Delete Debit Note',
      description: `Permanently delete ${note?.note_number || note?.debit_note_number}? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await debitNotesAPI.delete(noteId);
          toast.success('Debit note deleted');
          navigate(`/business/${bid}/debit-notes`);
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete debit note'));
        }
      },
    });
  };

  const handleEdit = () => {
    navigate(`/business/${bid}/debit-notes/${noteId}/edit`);
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await debitNotesAPI.pdf(noteId);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DN_${note?.note_number || note?.debit_note_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await debitNotesAPI.sendEmail(noteId);
      toast.success(response.data.message || 'Email sent');
      if (response.data.note) toast(response.data.note, { icon: 'info' });
      fetchNote();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send email'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsApp = async () => {
    try {
      const response = await debitNotesAPI.sendWhatsApp(noteId);
      toast.success(response.data.message || 'WhatsApp message sent');
      if (response.data.note) toast(response.data.note, { icon: 'info' });
      fetchNote();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send WhatsApp message'));
    }
  };

  const handleBack = () => {
    navigate(`/business/${bid}/debit-notes`);
  };

  /* -------- Helpers -------- */

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (val) =>
    (val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  /* -------- Early returns -------- */

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading debit note...</p>
        </div>
      </div>
    );
  }

  if (!note) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-[16px] text-slate-600 font-medium">Debit Note not found</p>
          <button onClick={handleBack} className="mt-4 text-teal-600 hover:underline text-[14px]">
            &larr; Back to Debit Notes
          </button>
        </div>
      </div>
    );
  }

  /* -------- Derived values -------- */

  const status = note.status || 'DRAFT';
  const badgeClass = STATUS_COLORS[status] || STATUS_COLORS.DRAFT;
  const noteNumber = note.note_number || note.debit_note_number || '--';
  const supplierName = note.supplier_name || note.party_name;
  const items = note.items || [];
  const adjustments = note.adjustments || [];
  const activityLog = note.activity_log || [];

  const iconBtnCls = 'inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98]';
  const showShare = status !== 'CANCELLED';

  const ShareButtons = () => (
    <>
      <button onClick={handleDownloadPdf} className={iconBtnCls} title="Download PDF"><Download className="w-4 h-4" /></button>
      {showShare && <button onClick={handleSendEmail} disabled={sendingEmail} className={`${iconBtnCls} disabled:opacity-50 disabled:cursor-not-allowed`} title="Send Email">{sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}</button>}
      {showShare && <button onClick={handleSendWhatsApp} className={iconBtnCls} title="Send WhatsApp"><MessageCircle className="w-4 h-4" /></button>}
    </>
  );

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={handleBack} className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95">
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-[20px] font-bold text-slate-800">{noteNumber}</h1>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold ${badgeClass}`}>{status}</span>
                </div>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  Created on {formatDate(note.created_at)} &bull; {items.length} item{items.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {status === 'DRAFT' && (
                <>
                  <button onClick={handleEdit} className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]">
                    <Edit className="w-4 h-4" /> Edit
                  </button>
                  <button onClick={handleIssue} className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20 active:scale-[0.98]">
                    <CheckCircle className="w-4 h-4" /> Issue
                  </button>
                  <div className="w-px h-8 bg-slate-200 mx-1"></div>
                  <ShareButtons />
                  <div className="w-px h-8 bg-slate-200 mx-1"></div>
                  <button onClick={handleDelete} className="inline-flex items-center gap-2 px-4 py-2.5 text-red-600 bg-white border border-red-300 rounded-xl hover:bg-red-50 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </>
              )}
              {status === 'ISSUED' && (
                <>
                  <ShareButtons />
                  <div className="w-px h-8 bg-slate-200 mx-1"></div>
                  <button onClick={handleCancel} className="inline-flex items-center gap-2 px-4 py-2.5 text-red-600 bg-white border border-red-300 rounded-xl hover:bg-red-50 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]">
                    <XCircle className="w-4 h-4" /> Cancel
                  </button>
                </>
              )}
              {status === 'ADJUSTED' && <ShareButtons />}
              {status === 'CANCELLED' && <ShareButtons />}
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT -- 3-6-3 Grid */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            {/* LEFT COLUMN (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Building className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Supplier</h2>
                </div>

                {supplierName ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-teal-100 flex items-center justify-center">
                        <span className="text-[16px] font-bold text-teal-700">
                          {supplierName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{supplierName}</p>
                        <p className="text-[12px] text-slate-500">Supplier</p>
                      </div>
                    </div>

                    {(note.supplier_gstin || note.party_gstin) && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Building className="w-4 h-4 text-slate-400" />
                        GSTIN: {note.supplier_gstin || note.party_gstin}
                      </div>
                    )}

                    {(note.supplier_phone || note.party_phone) && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {note.supplier_phone || note.party_phone}
                      </div>
                    )}

                    {(note.supplier_email || note.party_email) && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400" />
                        {note.supplier_email || note.party_email}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-[13px] text-slate-500">No supplier selected</p>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Note Info</h2>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Note Date</span>
                    <span className="text-[13px] font-medium text-slate-700">{formatDate(note.note_date || note.debit_note_date)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Reason</span>
                    <span className="text-[13px] font-medium text-slate-700">{note.reason_label || REASON_LABELS[note.reason] || note.reason || '--'}</span>
                  </div>
                  {(note.reference_invoice || note.reference_invoice_number) && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Ref. Invoice</span>
                      <span className="text-[13px] font-medium text-slate-700">{note.reference_invoice || note.reference_invoice_number}</span>
                    </div>
                  )}
                  {note.invoice_type && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Invoice Type</span>
                      <span className="text-[13px] font-medium text-slate-700">{note.invoice_type}</span>
                    </div>
                  )}
                  {(note.created_by_name || note.created_by) && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Created By</span>
                      <span className="text-[13px] font-medium text-slate-700">{note.created_by_name || note.created_by}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Created Date</span>
                    <span className="text-[13px] font-medium text-slate-700">{formatDate(note.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CENTER COLUMN (6 cols) */}
            <div className="col-span-12 lg:col-span-6 space-y-4">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                    <Package className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Items</h2>
                    <p className="text-[12px] text-slate-500">{items.length} item{items.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>

                {items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">#</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Item Name</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">HSN</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Qty</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Unit</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Rate</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Tax %</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Tax Amt</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50/50 transition-colors duration-150">
                            <td className="px-4 py-4 text-[13px] text-slate-400 font-medium">{index + 1}</td>
                            <td className="px-4 py-4 text-[14px] font-medium text-slate-800">{item.item_name || item.name || '--'}</td>
                            <td className="px-4 py-4 text-[13px] text-slate-500">{item.hsn_code || item.hsn || '--'}</td>
                            <td className="px-4 py-4 text-center">
                              <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 rounded-lg text-[13px] font-medium text-slate-700">
                                {item.quantity || 0}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-center text-[13px] text-slate-500">{item.unit || 'Nos'}</td>
                            <td className="px-4 py-4 text-right text-[13px] text-slate-600">{formatCurrency(item.rate)}</td>
                            <td className="px-4 py-4 text-right text-[13px] text-slate-600">{item.tax_rate || item.tax_percent || 0}%</td>
                            <td className="px-4 py-4 text-right text-[13px] text-slate-600">{formatCurrency(item.tax_amount)}</td>
                            <td className="px-4 py-4 text-right">
                              <span className="text-[14px] font-semibold text-emerald-600">{formatCurrency(item.amount)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-[14px] text-slate-500">No items in this debit note</p>
                  </div>
                )}

                {note.notes && (
                  <div className="px-5 py-4 bg-amber-50 border-t border-amber-100">
                    <p className="text-[11px] text-amber-600 uppercase tracking-wide font-semibold mb-1">Notes</p>
                    <p className="text-[13px] text-amber-800">{note.notes}</p>
                  </div>
                )}
              </div>

              {(note.reason_description || note.reason_details) && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                      <AlignLeft className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Reason Description</h2>
                  </div>
                  <p className="text-[13px] text-slate-700 leading-relaxed">{note.reason_description || note.reason_details}</p>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              <div className="rounded-2xl p-5 shadow-sm text-white bg-gradient-to-br from-violet-600 to-purple-700">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                    <IndianRupee className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold">Summary</h2>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-white/80">Subtotal</span>
                    <span className="font-medium">{formatCurrency(note.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-white/80">Tax</span>
                    <span className="font-medium">+ {formatCurrency(note.tax_total || note.tax_amount)}</span>
                  </div>
                  <div className="flex justify-between text-[15px] font-bold pt-2 border-t border-white/20">
                    <span>Grand Total</span>
                    <span>{formatCurrency(note.grand_total || note.total)}</span>
                  </div>
                  <div className="border-t border-white/20 pt-3 mt-3 space-y-2">
                    <div className="flex justify-between text-[13px]">
                      <span className="text-white/80">Adjusted Amount</span>
                      <span className="font-medium">{formatCurrency(note.adjusted_amount)}</span>
                    </div>
                    <div className="flex justify-between text-[13px]">
                      <span className="text-white/80">Balance Amount</span>
                      <span className="font-medium">{formatCurrency(note.balance_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {adjustments.length > 0 && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Adjustments</h2>
                  </div>

                  <div className="space-y-3">
                    {adjustments.map((adj, i) => (
                      <div key={i} className="p-3 bg-slate-50 rounded-xl">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-[13px] font-medium text-slate-700">{adj.type || adj.adjustment_type || '--'}</p>
                            {(adj.reference || adj.reference_number) && (
                              <p className="text-[11px] text-slate-500 mt-0.5">{adj.reference || adj.reference_number}</p>
                            )}
                          </div>
                          <span className="text-[13px] font-semibold text-emerald-600">{formatCurrency(adj.amount)}</span>
                        </div>
                        {adj.date && (
                          <p className="text-[11px] text-slate-400 mt-1">{formatDate(adj.date)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activityLog.length > 0 && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                      <Activity className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Activity</h2>
                  </div>

                  <div className="space-y-4">
                    {activityLog.map((entry, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                          <Clock className="w-3.5 h-3.5 text-slate-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] text-slate-700">
                            <span className="font-medium">{entry.user_name || 'System'}</span>
                            {entry.action && <> {entry.action}</>}
                          </p>
                          {entry.details && <p className="text-[12px] text-slate-500 mt-0.5">{entry.details}</p>}
                          {entry.timestamp && <p className="text-[11px] text-slate-400 mt-0.5">{formatDate(entry.timestamp)}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        confirmDisabled={confirmModal?.isCancelFlow ? !cancelReason.trim() : false}
        onConfirm={() => confirmModal?.onConfirm?.(cancelReason)}
        onClose={() => { setConfirmModal(null); setCancelReason(''); }}
      >
        {confirmModal?.isCancelFlow && (
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="e.g. Incorrect amounts, duplicate note, supplier dispute resolved..."
            rows={3}
            className="w-full mt-3 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 focus:outline-none"
          />
        )}
      </ConfirmModal>
    </div>
  );
};

export default DebitNoteDetailPage;
